<script lang="ts">
import { computed, defineComponent, h, ref, unref } from 'vue';

import { classNames } from '@components/utils';

import {
  makeDisabledProps,
  makeIconProps,
  makeInputProps,
  makeLocaleProps,
  makeReadonlyProps,
  makeTagProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useIcon,
  useInput,
  useLocale,
  useMergedState,
  useProxyModel,
  useRender
} from '../../composable';
import { CdsButton } from '../CdsButton';
import { CdsInput, CdsInputDescription, CdsInputLabel } from '../CdsInput';

import { AjaxUploader, UploadList } from './components';
import { makeUploadProps } from './composable/';
import { fileToObject, getFileItem, removeFileItem, updateFileList } from './utils';

import type { IUploadProps } from './composable/';
import type { NUploader } from './interface';
import type { Ref } from 'vue';
import type { ComponentProps } from '@components/utils';

export const LIST_IGNORE = `__LIST_IGNORE_${Date.now()}__`;

export const componentName = 'CdsUploader';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    ...makeUploadProps({
      action: '' as const,
      accept: '' as const,
      showUploadList: true,
      data: {},
      type: 'select' as const,
      listType: 'text' as const,
      multiple: true
    }),
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLocaleProps(),
    ...makeIconProps(),
    ...makeThemeProps(),
    ...makeTagProps({
      tag: 'span'
    })
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     * @type { fileList: NUploader.IUploadChangeParam<NUploader.IUploadFile>['fileList'] }
     */
    'update:modelValue': (fileList: NUploader.IUploadChangeParam<NUploader.IUploadFile>['fileList']) => true,
    /**
     * Событие изменения поля ввода
     * @type { info: NUploader.IUploadChangeParam<NUploader.IUploadFile> }
     */
    change: (info: NUploader.IUploadChangeParam<NUploader.IUploadFile>) => true,
    /**
     * Событие процесса загрузки файла
     * @type { event: NUploader.IUploadChangeParam['event'], file: NUploader.IUploadFile }
     */
    progress: (event: NUploader.IUploadChangeParam['event'], file: NUploader.IUploadFile) => true,
    /**
     * Событие успешной загрузки файла
     * @type { response: Record<string, unknown>, file: NUploader.IUploadFile }
     */
    success: (response: Record<string, unknown>, file: NUploader.IUploadFile) => true,
    /**
     * Событие ошибки загрузки файла
     * @type { error: NUploader.IUploadRequestError | ProgressEvent, response: any, file: NUploader.IUploadFile }
     */
    error: (error: NUploader.IUploadRequestError | ProgressEvent, response: any, file: NUploader.IUploadFile) => true,
    /**
     * Событие при удалении файла
     * @type { file: NUploader.IUploadFile }
     */
    remove: (file: NUploader.IUploadFile): void | boolean | Promise<void | boolean> => true,
    /**
     * Событие при drag-n-drop файла
     * @type { event: DragEvent }
     */
    drop: (event: DragEvent) => true,
    /**
     * Событие при отклонении загрузки файла
     * @type { fileList: NUploader.IUploadFile[] }
     */
    reject: (fileList: NUploader.IUploadFile[]) => true,
    /**
     * Событие при скачивании файла
     * @type { file: NUploader.IUploadFile }
     */
    download: (file: NUploader.IUploadFile) => true,
    /**
     * Событие при просмотре превью файла
     * @type { file: NUploader.IUploadFile }
     */
    preview: (file: NUploader.IUploadFile) => true
  },
  setup(props, { attrs, slots, emit, expose }) {
    const files = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { id, hasLabel, hasDescription } = useInput(props, slots, componentName);
    const { uploader: uploaderLocale } = useLocale(props);
    const { hasPrepend, hasAppend } = useIcon(props, slots, componentName);

    const [mergedFileList, setMergedFileList] = useMergedState(props.defaultFileList || [], {
      value: files as Ref<NUploader.IUploadFile[]>,
      postState: (list) => {
        const timestamp = Date.now();
        return (list ?? []).map((file, index) => {
          if (!file.uid && !Object.isFrozen(file)) {
            file.uid = `__AUTO__${timestamp}_${index}__`;
          }
          return file;
        });
      }
    });

    const dragState = ref('drop');

    const fieldRef = ref<HTMLElement>();
    const validationRef = ref<typeof CdsInput>();
    const uploadRef = ref<typeof AjaxUploader>();

    const hideMessages = computed(
      () => (!props.errorMessages.length && !props.messages.length && !slots.messages) || props.hideMessages
    );

    function onInternalChange(
      file: NUploader.IUploadFile,
      changedFileList: NUploader.IUploadFile[],
      event?: NUploader.IUploadChangeParam['event']
    ) {
      let cloneList = [...changedFileList];

      if (props.maxCount === 1) {
        cloneList = cloneList.slice(-1);
      } else if (props.maxCount) {
        cloneList = cloneList.slice(0, props.maxCount);
      }

      setMergedFileList(cloneList);

      const changeInfo: NUploader.IUploadChangeParam<NUploader.IUploadFile> = {
        file,
        fileList: cloneList
      };

      if (event) {
        changeInfo.event = event;
      }

      emit('update:modelValue', changeInfo.fileList);
      emit('change', changeInfo);
    }

    async function mergedBeforeUpload(
      file: NUploader.IFileType,
      fileListArgs: NUploader.IFileType[]
    ): Promise<File | false> {
      const { beforeUpload } = props;

      let parsedFile: NUploader.IFileType | Blob | string = file;
      if (beforeUpload) {
        const result = await beforeUpload(file, fileListArgs);

        if (result === false) return false;

        delete (file as any)[LIST_IGNORE];

        if ((result as any) === LIST_IGNORE) {
          Object.defineProperty(file, LIST_IGNORE, {
            value: true,
            configurable: true
          });

          return false;
        }

        if (typeof result === 'object' && result) {
          parsedFile = result as File;
        }
      }

      return parsedFile as File;
    }

    function onBatchStart(
      batchFileInfoList: {
        file: NUploader.IRcFile | null;
        parsedFile: Exclude<NUploader.TBeforeUploadFileType, boolean> | null;
      }[]
    ) {
      const filteredFileInfoList = batchFileInfoList.filter((fileInfo) => !(fileInfo.file as any)[LIST_IGNORE]);

      if (!filteredFileInfoList.length) return;

      const objectFileList = filteredFileInfoList.map((fileInfo) => fileToObject(fileInfo.file as NUploader.IFileType));

      let newFileList = [...(unref(mergedFileList) as NUploader.IUploadFile[])];

      objectFileList.forEach((fileObj) => {
        if (!fileObj) return;
        newFileList = updateFileList(fileObj, newFileList);
      });

      objectFileList.forEach((fileObj, index) => {
        if (!fileObj) return;

        let triggerFileObj: NUploader.IUploadFile = fileObj;

        if (!filteredFileInfoList[index].parsedFile) {
          const { originFileObj } = fileObj;

          let clone;

          try {
            clone = new File([originFileObj], originFileObj.name, {
              type: originFileObj.type
            }) as any as NUploader.IUploadFile;
          } catch (e) {
            clone = new Blob([originFileObj], {
              type: originFileObj.type
            }) as any as NUploader.IUploadFile;
            clone.name = originFileObj.name;
            clone.lastModifiedDate = new Date();
            clone.lastModified = new Date().getTime();
          }

          clone.uid = fileObj.uid;
          triggerFileObj = clone;
        } else {
          fileObj.status = 'uploading';
        }

        onInternalChange(triggerFileObj, newFileList);
      });
    }

    function onSuccess(response: any, file: NUploader.IFileType, xhr: XMLHttpRequest) {
      try {
        if (typeof response === 'string') {
          response = JSON.parse(response);
        }
      } catch (e) {
        /* do nothing */
      }

      if (!getFileItem(file, unref(mergedFileList) as NUploader.IUploadFile[])) return;

      const targetItem = fileToObject(file);

      if (!targetItem) return;

      targetItem.status = 'done';
      targetItem.percent = 100;
      targetItem.response = response;
      targetItem.xhr = xhr;

      const nextFileList = updateFileList(targetItem, unref(mergedFileList) as NUploader.IUploadFile[]);

      onInternalChange(targetItem, nextFileList);
      emit('success', response, targetItem);
    }

    function onProgress(event: NUploader.IUploadChangeParam['event'], file: NUploader.IFileType) {
      if (!file) return;
      if (!getFileItem(file, unref(mergedFileList) as NUploader.IUploadFile[])) return;

      const targetItem = fileToObject(file);

      if (!targetItem) return;

      targetItem.status = 'uploading';
      targetItem.percent = event!.percent;

      const nextFileList = updateFileList(targetItem, unref(mergedFileList) as NUploader.IUploadFile[]);

      onInternalChange(targetItem, nextFileList, event);
      emit('progress', event, targetItem);
    }

    function onError(
      error: NUploader.IUploadRequestError | ProgressEvent,
      response: any,
      file: NUploader.IFileType | null
    ) {
      if (!file) return;
      if (!getFileItem(file, unref(mergedFileList) as NUploader.IUploadFile[])) return;

      const targetItem = fileToObject(file);

      if (!targetItem) return;

      targetItem.error = error;
      targetItem.response = response;
      targetItem.status = 'error';

      const nextFileList = updateFileList(targetItem, unref(mergedFileList) as NUploader.IUploadFile[]);

      onInternalChange(targetItem, nextFileList);
      emit('error', error, response, targetItem);
    }

    function handleDownload(file: NUploader.IUploadFile) {
      emit('download', file);
    }

    function handleRemove(file: NUploader.IUploadFile) {
      let currentFile: NUploader.IUploadFile;

      Promise.resolve(emit('remove', file)).then((result: any) => {
        if (result === false) return;

        const removedFileList = removeFileItem(file, unref(mergedFileList) as NUploader.IUploadFile[]);

        if (removedFileList) {
          currentFile = { ...file, status: 'removed' };
          unref(mergedFileList)?.forEach((item) => {
            const matchKey = currentFile.uid !== undefined ? 'uid' : 'name';
            if (item[matchKey] === currentFile[matchKey] && !Object.isFrozen(item)) {
              item.status = 'removed';
            }
          });

          unref(uploadRef)?.abort(currentFile);

          onInternalChange(currentFile, removedFileList);
        }
      });
    }

    function handlePreview(file: NUploader.IUploadFile) {
      emit('preview', file);
    }

    function onFileDrop(event: DragEvent) {
      dragState.value = event.type;
      if (event.type === 'drop') {
        props.onDrop?.(event);
      }
    }

    useRender(() => {
      const { class: className, style: styleName, ...spreadAttrs } = attrs;
      const ajaxUploadProps: IUploadProps | any = {
        ...props,
        ...spreadAttrs,
        id: unref(id),
        beforeUpload: mergedBeforeUpload,
        onBatchStart,
        onError,
        onProgress,
        onSuccess,
        onChange: undefined
      };

      const uploadBtnProps: ComponentProps<typeof CdsButton> = {
        appearance: 'primary',
        text: uploaderLocale.uploadFile,
        prependIcon: props.uploadIcon,
        ...props.buttonProps,
        disabled: props.disabled,
        readonly: props.readonly
      };

      const renderUploadButton = () => {
        return h(
          AjaxUploader,
          { ...ajaxUploadProps, ref: uploadRef },
          {
            default: () => (slots.default ? slots.default?.({ props: uploadBtnProps }) : h(CdsButton, uploadBtnProps))
          }
        );
      };
      const renderUploadList = () => {
        const {
          listType,
          locale,
          disabled,
          removeIcon,
          downloadIcon,
          isImageUrl,
          showUploadList,
          itemRender,
          showTooltipWithError
        } = props;
        const { showDownloadIcon, showRemoveIcon } =
          typeof showUploadList === 'boolean' ? ({} as NUploader.TShowUploadList) : showUploadList;

        return (
          showUploadList &&
          h(
            UploadList,
            {
              listType,
              items: unref(mergedFileList),
              itemRender,
              showRemoveIcon: !disabled && showRemoveIcon,
              showDownloadIcon,
              removeIcon,
              downloadIcon,
              isImageUrl,
              showUploadList,
              showTooltipWithError,
              locale,
              onPreview: handlePreview,
              onDownload: handleDownload,
              onRemove: handleRemove
            },
            {
              itemRender: slots.itemRender
            }
          )
        );
      };

      const prependSlot = unref(hasPrepend) ? { prepend: () => slots.prepend?.() } : {};
      const appendSlot = unref(hasAppend) ? { append: () => slots.append?.() } : {};

      return h('div', { ref: fieldRef, class: classNames('cds-uploader-field', unref(themeClasses)) }, [
        unref(hasLabel) && h(CdsInputLabel, { for: unref(id), text: props.label }, { default: slots.label }),
        unref(hasDescription) && h(CdsInputDescription, { text: props.description }, { default: slots.description }),
        h(
          CdsInput,
          {
            ...attrs,
            ref: validationRef,
            id: unref(id),
            modelValue: files,
            disabled: props.disabled,
            readonly: props.readonly,
            errorMessages: props.errorMessages,
            messages: props.messages,
            maxErrors: props.maxErrors,
            name: props.name,
            rules: props.rules,
            validateOn: props.validateOn,
            validationValue: props.validationValue,
            hideMessages: unref(hideMessages),
            persistentMessages: props.persistentMessages,
            prependIcon: props.prependIcon,
            appendIcon: props.appendIcon,
            class: classNames('cds-uploader', attrs.class),
            'onUpdate:modelValue': (value) => {
              files.value = value;
            }
          },
          {
            ...prependSlot,
            default: () => [renderUploadButton()],
            messages: () => slots.messages?.(),
            ...appendSlot
          }
        ),
        renderUploadList()
      ]);
    });

    expose({
      onBatchStart,
      onSuccess,
      onProgress,
      onError,
      fileList: mergedFileList,
      uploadRef
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsUploader';
</style>
